import {css, html, LitElement, PropertyValues} from 'lit';
import {customElement, property} from 'lit/decorators';
import {containerStyles, normalizeStyles} from '@webs/components-library/theming';
import {MultiSelectionFilter} from '@webs/components-library/filters-dropdown';

@customElement('webs-filters-dropdown-mobile')
export class FiltersDropdownMobile extends LitElement {
  private filters: Map<string, string[]> = new Map();
  private activeFilters: unknown = '';
  private filtersCount = 0;
  private internalLimit: number;
  private internalOffset = 0;
  private afterToken: string;
  private results: Record<string, unknown>[];
  private totalCount = 0;

  static styles = [
    normalizeStyles,
    containerStyles,
    css`
      a {
        font-family: var(--font-family-alternative);
        font-size: .8rem;
        font-weight: var(--font-weight-strong);
        line-height: 1.2;
        text-decoration: none;
      }

      .filters__wrapper {
        background: var(--color-light-grey);
        display: none;
        height: 100%;
        left: 0;
        position: fixed;
        top: 0;
        width: 100%;
        z-index: 999;
      }

      .filter__trigger {
        line-height: 1.3;
        text-transform: uppercase;
      }

      .filters__wrapper.show {
        display: block;
      }

      .filters__wrapper .container {
        text-align: center;
      }

      .actions {
        align-items: center;
        display: flex;
        justify-content: space-between;
        padding: 1rem 0 1.25rem;
      }

      .actions a {
        font-size: .7rem;
      }

      .actions a:hover {
        color: var(--color-primary);
      }

      .actions a:first-child {
        color: var(--color-base);
        display: flex;
      }

      .actions a:first-child span {
        position: relative;
        display: block;
        height: 14px;
        margin-right: .37rem;
        transform: translateY(2px);
        width: 14px;
      }

      .actions a:first-child span::before,
      .actions a:first-child span::after {
        background: var(--color-base);
        content: '';
        display: inline-block;
        height: 1.5px;
        left: 50%;
        position: absolute;
        top: 50%;
        width: 100%;
      }

      .actions a:first-child span::before {
        transform: translate(-50%, -50%) rotate(-45deg);
      }

      .actions a:first-child span::after {
        transform: translate(-50%, -50%) rotate(45deg);
      }

      .actions a:last-child {
        color: var(--color-grey);
      }

      .search {
        background: var(--color-base);
        border-radius: 2.1rem;
        color: var(--color-white);
        cursor: pointer;
        display: inline-block;
        font: var(--font-weight-strong) .8rem/1.3 var(--font-family-alternative);
        outline: none;
        overflow: hidden;
        margin-top: 2rem;
        padding: .65rem 1.5rem;
        position: relative;
        text-decoration: none;
        transition: background .1s linear, border .1s linear, color .1s linear;
        z-index: 1;
      }

      .active-filters {
        align-items: baseline;
        display: flex;
        margin: 0.8rem -0.3rem -.5rem;
        overflow-x: auto;
        padding-bottom: .5rem;
      }

      .active-filters p {
        align-items: center;
        background: var(--color-light-grey);
        border-radius: 2.1rem;
        display: inline-flex;
        font-family: var(--font-family-alternative);
        font-feature-settings: 'salt' on, 'liga' off;
        font-size: .7rem;
        line-height: 1.3;
        margin: 0 .3rem;
        padding: .3rem .8rem;
        white-space: nowrap;
      }

      .active-filters a {
        display: inline-block;
        flex: 0 0 14px;
        height: 14px;
        margin-left: 2px;
        position: relative;
        width: 14px;
      }

      .active-filters a::after,
      .active-filters a::before {
        background: var(--color-base);
        content: '';
        height: 14px;
        left: 50%;
        position: absolute;
        top: 50%;
        width: 1.5px;
      }

      .active-filters a::after {
        transform: translate(-50%, -50%) rotate(45deg);
      }

      .active-filters a::before {
        transform: translate(-50%, -50%) rotate(-45deg);
      }
    `
  ];

  @property({attribute: 'url'})
  rootUrl: string;

  @property({attribute: 'close-text'})
  closeText = 'Filters';

  @property({attribute: 'reset-text'})
  resetText = 'Clear all';

  @property({attribute: 'reset-text'})
  searchText = 'results';

  @property({attribute: 'show', type: Boolean})
  showFilters: boolean;

  @property({type: Number})
  set limit(value: number) {
    const oldValue = this.internalLimit;
    this.internalLimit = value;
    this.requestUpdate('limit', oldValue);
  }

  get limit(): number {
    return this.internalLimit;
  }

  @property({type: Number})
  set offset(value: number) {
    const oldValue = this.internalOffset;
    this.internalOffset = value;
    this.requestUpdate('offset', oldValue);
  }

  get offset(): number {
    return this.internalOffset;
  }

  protected render(): unknown {
    return html`
      <section class="filters">
        ${this.filtersCount > 0 ? html`
          <a class="filter__trigger" @click="${this.show}">Filter
            ${this.filtersCount > 0 ? `(${this.filtersCount})` : ''}
          </a>

          <div class="active-filters">
            ${this.activeFilters}
          </div>
        ` : html`
          <a class="filter__trigger" @click="${this.show}">Filter</a>
        `}

        <div class="filters__wrapper ${this.showFilters ? 'show' : ''}">
          <div class="container">
            <div class="actions">
              <a href="javascript:;" @click="${this.close}"><span></span>${this.closeText}</a>
              <a href="javascript:;" @click="${this.resetFilters}">${this.resetText}</a>
            </div>
            <slot></slot>

            <a class="search" @click="${this.onSearch}">See all ${this.searchText}</a>
          </div>
        </div>
      </section>
    `
  }

  protected update(changedProperties: PropertyValues) {
    if (changedProperties.size === 1 && changedProperties.has('offset')) {
      this.onSearch();
    }
    super.update(changedProperties);
  }

  private onSearch(): void {
    this.dispatchResultsPage(true, true);
    this.showFilters = false;
    this.requestUpdate();
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    document.addEventListener('DOMContentLoaded', evt => {
      this.onSearch();
      const items = this.shadowRoot.querySelector('slot').assignedElements();
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        item.addEventListener('filteradd', (e: CustomEvent) => {
          const filter: MultiSelectionFilter = e.detail;
          this.filters.set(filter.key, filter.activeFilters);
          this.internalOffset = 0;
          delete this.results;
          this.totalCount = 0;
          this.dispatchResultsPage(filter.isRemove, filter.isRemove);
        });
      }
    });
  }

  private close(event: Event): void {
    event.preventDefault();
    this.showFilters = false;
    this.requestUpdate();
  }

  private show(event: Event): void {
    event.preventDefault();
    this.showFilters = true;
    this.requestUpdate();
  }

  private resetFilters(event: Event): void {
    event.preventDefault();
    const resetFilterEvent = new CustomEvent('resetfilterall', {
      detail: {noFilters: true},
      bubbles: true,
      composed: true
    });
    this.dispatchEvent(resetFilterEvent);
    this.requestUpdate();
  }

  private removeFilter(event: Event): void {
    event.preventDefault();
    const target: HTMLElement = event.target as HTMLElement;
    const resetFilterEvent = new CustomEvent('filterremove', {
      detail: {filter: target.dataset.filterSlug, key: target.dataset.filterKey},
      bubbles: true,
      composed: true
    });
    this.dispatchEvent(resetFilterEvent);
    this.requestUpdate();
  }

  private dispatchResultsPage(dispatchEvent: boolean, updateFilters = false): void {
    const requiredCount = this.offset + this.limit;
    if(Array.isArray(this.results) && (this.results.length >= requiredCount || requiredCount > this.totalCount )){
      const currentResults = this.results.slice(this.offset, requiredCount);
      this.dispatchResourcesChangeEvent(currentResults, this.totalCount);
    } else {
      this.getData(dispatchEvent, updateFilters);
    }
  }

  private dispatchResourcesChangeEvent(results: Record<string, unknown>[], total: number): void {
    this.dispatchEvent(new CustomEvent('resourceschange', {
      bubbles: true, detail: {
        objects: results,
        totalCount: total
      }
    }));
  }

  private getData(dispatchEvent: boolean, updateFilters = false): void {
    const xhttp = new XMLHttpRequest();
    xhttp.onreadystatechange = () => {
      if (xhttp.readyState == 4 && xhttp.status == 200) {
        const data = JSON.parse(xhttp.responseText);
        this.shadowRoot.querySelector<HTMLAnchorElement>('.search').innerText = `See ${data.total} ${this.searchText}`;
        if (dispatchEvent) {
          this.dispatchEvent(new CustomEvent('searchcomplete'));

          this.afterToken = data.paging ? data.paging.next.after : undefined;
          if(!Array.isArray(this.results)){
            this.results = [];
          }

          this.totalCount = data.total;
          this.results.push(... data.results);


          this.dispatchResultsPage(dispatchEvent, updateFilters);
        }
      }
      //TODO: error handling
    };
    this.filtersCount = 0;
    this.activeFilters = '';

    const urlSearchParams = new URLSearchParams();
    for (const name of this.filters.keys()) {
      const value = this.filters.get(name);
      if (value.length === 0) {
        continue;
      }
      const filterValues: string[] = this.filters.get(name);
      urlSearchParams.set(`${name}__in`, filterValues.join(','));

      if (updateFilters) {
        this.renderFilter(name, filterValues);
      }
    }

    if (this.limit) {
      const resultsLimit = this.limit * 10;
      urlSearchParams.set('limit', resultsLimit.toString());
    }

    if (this.afterToken){
      urlSearchParams.set('after', this.afterToken);
    }

    //if (this.offset) {
    //  urlSearchParams.set('offset', this.offset.toString());
    //}

    let url = this.rootUrl;
    const searchParams = urlSearchParams.toString();
    if (searchParams.length > 0 && !url.includes('?')) {
      url = `${url}?`;
    } else if (!url.endsWith('?')) {
      url = `${url}&`;
    }

    url = `${url}${searchParams}`;

    xhttp.open('GET', url, true);
    xhttp.send();
    if(updateFilters) {
      this.requestUpdate();
    }
  }

  private renderFilter(name: string, filterValues: string[]): void {
    this.filtersCount += filterValues.length;
    for (const value of filterValues) {
      this.activeFilters = html`${this.activeFilters} <p>${value} <a  href="javascript:;" @click="${this.removeFilter}" data-filter-key="${name}" data-filter-slug="${value}"></a></p>`;
    }
  }
}
